import { Card, Flex } from 'antd';
import { BaseButton } from 'components/button/button-base';
import { Text } from 'components/mvp-typography';
import { useCreateCheckoutSessionMutation } from 'lib/hooks/tanstack/use-billing-createCheckoutSessionMutation';
import { useClinicianContext } from 'lib/hooks/use-resolveCurrentClinicInfo';
import { Check } from 'lucide-react';

export interface CheckoutSessionCardProps {
  productId: string;
  title: string;
  price: string;
  priceFrequency?: string;
  priceSubtext?: string;
  features?: string[];
  isPopular?: boolean;
  buttonText?: string;
  redirectUrl?: string;
}

export function CheckoutSessionCard(props: CheckoutSessionCardProps) {
  const { currentClinic } = useClinicianContext();
  const mutation = useCreateCheckoutSessionMutation();

  const priceFrequency = props.priceFrequency;
  const features = props.features !== undefined ? props.features : [];
  const isPopular = props.isPopular !== undefined ? props.isPopular : false;
  const buttonText = props.buttonText ?? 'Start Now';

  const handleCheckout = () => {
    mutation.mutate(
      {
        clinicId: currentClinic,
        productId: props.productId,
        redirectUrl: props.redirectUrl || window.location.href
      },
      {
        onSuccess: res => {
          if (res.data?.url) window.location.href = res.data.url;
        }
      }
    );
  };

  return (
    <Card
      style={{
        height: '100%',
        maxWidth: '245px',
        borderRadius: '16px',
        border: '1px solid #E4E6EB',
        backgroundColor: isPopular ? '#F4F5F7' : '#FFFFFF',
        boxShadow: isPopular ? '0 4px 12px rgba(0,0,0,0.05)' : 'none',
        overflow: 'hidden'
      }}
    >
      <Flex vertical gap={20} align="flex-start">
        <Flex gap={8} wrap="wrap" style={{ minHeight: '24px' }}>
          {isPopular && (
            <span
              style={{
                backgroundColor: '#FFFFFF',
                color: '#1F2021',
                border: '1px solid #E4E6EB',
                borderRadius: '4px',
                padding: '2px 8px',
                fontSize: '12px',
                fontWeight: '600'
              }}
            >
              Most popular
            </span>
          )}
          <span
            style={{
              color: '#FFFFFF',
              borderRadius: '4px',
              padding: '2px 8px',
              fontSize: '12px',
              fontWeight: '600',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px'
            }}
          ></span>
        </Flex>

        <Text
          style={{
            // fontSize: '20px',
            fontWeight: '700',
            color: '#000000',
            margin: '4px 0 0 0',
            lineHeight: 1.2
          }}
        >
          {props.title}
        </Text>

        <Flex vertical gap={4} style={{ width: '100%' }}>
          <Flex align="baseline" gap={4}>
            <Text
              style={{
                fontSize: '24px',
                fontWeight: '800',
                color: '#000000',
                lineHeight: 1.1,
                whiteSpace: 'nowrap'
              }}
            >
              {props.price}
            </Text>
            {priceFrequency && (
              <Text
                style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#8A8C90'
                }}
              >
                {priceFrequency}
              </Text>
            )}
          </Flex>
          {props.priceSubtext && (
            <Text
              style={{
                fontSize: '13px',
                fontWeight: '400',
                color: '#8A8C90',
                margin: 0
              }}
            >
              {props.priceSubtext}
            </Text>
          )}
        </Flex>

        <BaseButton
          loading={mutation.isPending}
          onClick={handleCheckout}
          style={{
            width: '100%',
            height: '44px',
            borderRadius: '100px',
            fontSize: '16px',
            fontWeight: '600',
            marginTop: '8px',
            boxShadow: 'none'
          }}
        >
          {buttonText}
        </BaseButton>

        {features.length > 0 && (
          <Flex vertical gap={12} style={{ width: '100%', marginTop: '8px' }}>
            <Text
              style={{ fontSize: '14px', fontWeight: '600', color: '#000000' }}
            >
              This includes:
            </Text>
            <Flex vertical gap={10}>
              {features.map((feature, idx) => (
                <Flex key={idx} align="center" gap={10}>
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#A0A3A6',
                      color: '#FFFFFF',
                      borderRadius: '50%',
                      width: '18px',
                      height: '18px',
                      flexShrink: 0
                    }}
                  >
                    <Check size={11} style={{ strokeWidth: 3 }} />
                  </span>
                  <Text
                    style={{
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#4B4D52'
                    }}
                  >
                    {feature}
                  </Text>
                </Flex>
              ))}
            </Flex>
          </Flex>
        )}

        {mutation.isError && (
          <Text
            style={{
              color: '#FF4D4F',
              fontSize: '13px',
              fontWeight: '500',
              textAlign: 'center',
              width: '100%',
              marginTop: '4px'
            }}
          >
            {mutation.error instanceof Error
              ? mutation.error.message
              : 'An error occurred. Please try again.'}
          </Text>
        )}
      </Flex>
    </Card>
  );
}
